import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Input,
  Spinner,
} from '@heroui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { useLocale } from '../contexts/LocaleContext';
import { orderService } from '../services/orderService';
import { GuestOrderTracking } from '../types/orders';

/** Poll back-off delays in ms: 1s, 5s, 10s, 30s — then stop */
const POLL_DELAYS_MS = [1000, 5000, 10000, 30000];

const GuestOrderTrackingPage: React.FC = () => {
  const pageTitle = 'Guest Order Tracking';
  const pageDescription =
    'Track your guest order status with your order number and email address.';
  const { trackingToken } = useParams<{ trackingToken: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { formatCurrency, convertPrice } = useLocale();

  const [orderData, setOrderData] = useState<GuestOrderTracking | null>(null);
  const [loading, setLoading] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<{
    orderNumber?: string;
    email?: string;
  }>({});
  const pollAttemptRef = useRef(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasPollingStartedRef = useRef(false);
  const lastOrderNumberRef = useRef<string | null>(null);

  const handleTrackOrderWithValues = useCallback(
    async (orderNum: string, emailAddr: string) => {
      try {
        setLoading(true);
        const order = await orderService.trackGuestOrder(orderNum, emailAddr);
        setOrderData(order);
        setErrors({});
      } catch (error: any) {
        toast.error(
          'Order not found. Please check your order number and email address.'
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchOrderByToken = useCallback(async (token: string) => {
    try {
      setLoading(true);
      const order = await orderService.getGuestOrderByToken(token);
      setOrderData(order);
    } catch (error: any) {
      toast.error('Order not found. Please check your tracking information.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If tracking token is provided in URL, fetch the order directly
    if (trackingToken) {
      fetchOrderByToken(trackingToken);
    }

    // If email/order number are provided in location state, pre-fill them
    if (location.state?.email) {
      setEmail(location.state.email);
    }
    if (location.state?.orderNumber) {
      setOrderNumber(location.state.orderNumber);
    }

    // Also read from query params so refresh/back works
    const queryParams = new URLSearchParams(location.search);
    const queryOrderNumber = queryParams.get('orderNumber');
    const queryEmail = queryParams.get('email');
    if (queryOrderNumber) {
      setOrderNumber(queryOrderNumber);
    }
    if (queryEmail) {
      setEmail(queryEmail);
    }

    if (queryOrderNumber && queryEmail) {
      handleTrackOrderWithValues(queryOrderNumber, queryEmail);
    }
  }, [
    trackingToken,
    location.state,
    location.search,
    handleTrackOrderWithValues,
    fetchOrderByToken,
  ]);

  // Poll for updated order status when payment is not yet paid
  useEffect(() => {
    if (!orderData) {
      hasPollingStartedRef.current = false;
      lastOrderNumberRef.current = null;
      return;
    }

    if (orderData.paymentStatus.toLowerCase() === 'paid') {
      hasPollingStartedRef.current = false;
      return;
    }

    // Reset polling state when tracking a different order
    if (orderData.orderNumber !== lastOrderNumberRef.current) {
      hasPollingStartedRef.current = false;
      pollAttemptRef.current = 0;
      lastOrderNumberRef.current = orderData.orderNumber;
    }

    if (hasPollingStartedRef.current) {
      return;
    }

    const canRefetch =
      !!trackingToken || (!!orderNumber.trim() && !!email.trim());
    if (!canRefetch) {
      return;
    }

    hasPollingStartedRef.current = true;

    const refetchOrder = async (): Promise<GuestOrderTracking | null> => {
      try {
        if (trackingToken) {
          return await orderService.getGuestOrderByToken(trackingToken);
        }
        return await orderService.trackGuestOrder(orderNumber, email);
      } catch {
        return null;
      }
    };

    const scheduleNextPoll = () => {
      if (pollAttemptRef.current >= POLL_DELAYS_MS.length) {
        pollTimerRef.current = null;
        return;
      }
      const delay = POLL_DELAYS_MS[pollAttemptRef.current];
      pollAttemptRef.current += 1;
      pollTimerRef.current = setTimeout(async () => {
        const updated = await refetchOrder();
        if (updated) {
          setOrderData(updated);
          if (updated.paymentStatus.toLowerCase() === 'paid') {
            pollAttemptRef.current = POLL_DELAYS_MS.length;
            pollTimerRef.current = null;
            return;
          }
        }
        scheduleNextPoll();
      }, delay);
    };

    scheduleNextPoll();

    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [
    orderData?.id,
    orderData?.orderNumber,
    orderData?.paymentStatus,
    trackingToken,
    orderNumber,
    email,
  ]);

  const validateForm = (): boolean => {
    const newErrors: { orderNumber?: string; email?: string } = {};

    if (!orderNumber.trim()) {
      newErrors.orderNumber = 'Order number is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTrackOrder = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const order = await orderService.trackGuestOrder(orderNumber, email);
      setOrderData(order);
      setErrors({});
    } catch (error: any) {
      toast.error(
        'Order not found. Please check your order number and email address.'
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'text-yellow-600';
      case 'processing':
      case 'printing':
      case 'printed':
        return 'text-blue-600';
      case 'shipped':
        return 'text-purple-600';
      case 'delivered':
        return 'text-green-600';
      case 'cancelled':
      case 'refunded':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getPaymentStatusColor = (paymentStatus: string) => {
    switch (paymentStatus.toLowerCase()) {
      case 'paid':
        return 'text-green-600';
      case 'pending':
      case 'processing':
        return 'text-yellow-600';
      case 'failed':
      case 'cancelled':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatAmount = (amount: number) => formatCurrency(convertPrice(amount));

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900'>Track Your Order</h1>
          <p className='text-gray-600 mt-2'>
            Enter your order number and email to track your order status
          </p>
        </div>

        {!orderData && (
          <Card className='mb-8'>
            <CardHeader>
              <h2 className='text-xl font-semibold'>Order Tracking</h2>
            </CardHeader>
            <CardBody className='space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Input
                  label='Order Number'
                  placeholder='CG20250101-0001'
                  value={orderNumber}
                  onValueChange={setOrderNumber}
                  isInvalid={!!errors.orderNumber}
                  errorMessage={errors.orderNumber}
                  isRequired
                />
                <Input
                  label='Email Address'
                  type='email'
                  placeholder='your@email.com'
                  value={email}
                  onValueChange={setEmail}
                  isInvalid={!!errors.email}
                  errorMessage={errors.email}
                  isRequired
                />
              </div>
              <Button
                color='primary'
                size='lg'
                onClick={handleTrackOrder}
                isLoading={loading}
                disabled={loading}
                className='w-full md:w-auto'
              >
                Track Order
              </Button>
            </CardBody>
          </Card>
        )}

        {loading && !orderData && (
          <div className='flex justify-center items-center py-12'>
            <Spinner size='lg' />
          </div>
        )}

        {orderData && (
          <div className='space-y-6'>
            {/* Order Status Overview */}
            <Card className='relative'>
              {orderData.paymentStatus.toLowerCase() !== 'paid' && (
                <div className='absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 rounded-lg'>
                  <Spinner size='lg' />
                  <p className='mt-3 text-sm text-gray-600'>
                    Waiting for payment confirmation...
                  </p>
                  <p className='mt-1 text-xs text-gray-500'>
                    This page will update automatically
                  </p>
                </div>
              )}
              <CardHeader>
                <h2 className='text-xl font-semibold'>
                  Order #{orderData.orderNumber}
                </h2>
              </CardHeader>
              <CardBody className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div>
                    <p className='text-sm text-gray-500'>Order Status</p>
                    <p
                      className={`text-lg font-semibold ${getStatusColor(
                        orderData.status
                      )}`}
                    >
                      {orderData.status}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>Payment Status</p>
                    <p
                      className={`text-lg font-semibold ${getPaymentStatusColor(
                        orderData.paymentStatus
                      )}`}
                    >
                      {orderData.paymentStatus}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>Order Date</p>
                    <p className='text-lg font-semibold'>
                      {new Date(orderData.orderDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {orderData.trackingNumber && (
                  <div className='mt-4 p-4 bg-blue-50 rounded-lg'>
                    <p className='text-sm text-gray-500'>Tracking Number</p>
                    <p className='text-lg font-semibold text-blue-600'>
                      {orderData.trackingNumber}
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Order Items */}
            <Card>
              <CardHeader>
                <h3 className='text-lg font-semibold'>Order Items</h3>
              </CardHeader>
              <CardBody>
                <div className='space-y-4'>
                  {orderData.items.map((item, index) => (
                    <div
                      key={index}
                      className='flex justify-between items-center'
                    >
                      <div>
                        <h4 className='font-medium'>{item.productName}</h4>
                        <p className='text-gray-500'>
                          {formatAmount(item.price)} × {item.quantity}
                        </p>
                      </div>
                      <span className='font-semibold'>
                        {formatAmount(item.totalPrice)}
                      </span>
                    </div>
                  ))}

                  <Divider />

                  <div className='flex justify-between items-center text-lg font-semibold'>
                    <span>Total</span>
                    <span>{formatAmount(orderData.totalAmount)}</span>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Shipping Information */}
            <Card>
              <CardHeader>
                <h3 className='text-lg font-semibold'>Shipping Information</h3>
              </CardHeader>
              <CardBody>
                <div className='space-y-2'>
                  <p className='font-medium'>{orderData.shippingName}</p>
                  <div className='text-gray-600'>
                    <p>{orderData.shippingAddress}</p>
                    <p>
                      {orderData.shippingCity}, {orderData.shippingState}{' '}
                      {orderData.shippingZipCode}
                    </p>
                    <p>{orderData.shippingCountry}</p>
                  </div>
                </div>

                {orderData.shippedDate && (
                  <div className='mt-4 p-4 bg-green-50 rounded-lg'>
                    <p className='text-sm text-gray-500'>Shipped Date</p>
                    <p className='font-semibold text-green-600'>
                      {new Date(orderData.shippedDate).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {orderData.deliveredDate && (
                  <div className='mt-4 p-4 bg-green-50 rounded-lg'>
                    <p className='text-sm text-gray-500'>Delivered Date</p>
                    <p className='font-semibold text-green-600'>
                      {new Date(orderData.deliveredDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Order Tracking */}
            {orderData.tracking && (
              <Card>
                <CardHeader>
                  <h3 className='text-lg font-semibold'>Tracking History</h3>
                </CardHeader>
                <CardBody>
                  <div className='space-y-4'>
                    {orderData.tracking.events.map((event, index) => (
                      <div key={index} className='flex items-start space-x-4'>
                        <div className='flex-shrink-0 w-3 h-3 bg-blue-600 rounded-full mt-1' />
                        <div>
                          <p className='font-medium'>{event.status}</p>
                          <p className='text-gray-600'>{event.description}</p>
                          <p className='text-sm text-gray-500'>
                            {new Date(event.date).toLocaleDateString()} at{' '}
                            {new Date(event.date).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Actions */}
            <div className='flex flex-col sm:flex-row gap-4'>
              <Button
                variant='bordered'
                onClick={() => {
                  setOrderData(null);
                  setOrderNumber('');
                  setEmail('');
                  setErrors({});
                }}
              >
                Track Another Order
              </Button>
              <Button color='primary' onClick={() => navigate('/search')}>
                Continue Shopping
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GuestOrderTrackingPage;
